import { useTranslation } from 'react-i18next'
import { useModelDetail } from '../hooks'

interface ModelDetailPanelProps {
  readonly provider: string
  readonly externalId: string
  readonly onClose: () => void
}

function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB'] as const
  let size = bytes
  let unitIndex = 0
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`
}

function formatNumber(n: number | null): string {
  if (n === null) return '—'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

export function ModelDetailPanel({ provider, externalId, onClose }: ModelDetailPanelProps) {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useModelDetail(provider, externalId)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-t-transparent border-tg-hint"
        />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-tg-link transition-opacity hover:opacity-80 active:opacity-60"
        >
          {t('common.back')}
        </button>
        <p className="text-tg-hint">
          {t('search.detail.loadError')}
        </p>
      </div>
    )
  }

  const model = data

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-tg-link transition-opacity hover:opacity-80 active:opacity-60"
        >
          {t('common.back')}
        </button>
      </div>

      {/* Preview images */}
      {model.preview_images.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {model.preview_images.map((url) => (
            <img
              key={url}
              src={url}
              alt={model.name}
              className="h-48 w-auto flex-shrink-0 rounded-xl border border-tg-hint/10 object-cover shadow-md"
            />
          ))}
        </div>
      )}

      {/* Title & author */}
      <div>
        <h2 className="text-lg font-semibold text-tg-text">
          {model.name}
        </h2>
        {model.author !== null && (
          <p className="text-sm text-tg-hint">
            {t('search.detail.by')} {model.author}
          </p>
        )}
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-2">
        <span className="rounded-full bg-tg-button px-2 py-0.5 text-xs font-medium text-tg-button-text">
          {model.resource_type}
        </span>
        <span className="rounded-full bg-tg-secondary px-2 py-0.5 text-xs text-tg-hint">
          {model.provider}
        </span>
      </div>

      {/* Stats */}
      <div className="flex gap-6 text-sm text-tg-hint">
        {model.download_count !== null && (
          <span>{t('search.detail.downloads')}: {formatNumber(model.download_count)}</span>
        )}
        {model.rating !== null && (
          <span>{t('search.detail.rating')}: {model.rating.toFixed(1)}</span>
        )}
      </div>

      {/* Description */}
      {model.description !== null && model.description.length > 0 && (
        <div className="rounded-xl border border-tg-hint/10 bg-tg-secondary p-3 text-sm text-tg-text shadow-sm">
          <p className="whitespace-pre-line">{model.description}</p>
        </div>
      )}

      {/* Tags */}
      {model.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {model.tags.map((tag) => (
            <span
              key={tag}
              className="rounded bg-tg-secondary px-1.5 py-0.5 text-xs text-tg-hint"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Versions */}
      {model.versions.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-medium text-tg-text">
            {t('search.detail.versions')} ({model.versions.length})
          </h3>
          <div className="flex flex-col gap-2">
            {model.versions.map((version) => (
              <div
                key={version.version_id}
                className="flex items-center justify-between rounded-xl border border-tg-hint/10 bg-tg-secondary p-3 shadow-sm"
              >
                <div>
                  <p className="text-sm font-medium text-tg-text">
                    {version.name}
                  </p>
                  <p className="text-xs text-tg-hint">
                    {formatFileSize(version.file_size_bytes)}
                    {version.created_at !== null && ` · ${version.created_at}`}
                  </p>
                </div>
                {version.download_url !== null && (
                  <a
                    href={version.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg bg-gradient-to-br from-tg-button to-tg-button/80 px-3 py-1.5 text-xs font-medium text-tg-button-text shadow-sm shadow-tg-button/20 transition-all duration-200 active:scale-[0.98]"
                  >
                    {t('search.detail.download')}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main download link */}
      {model.download_url !== null && (
        <a
          href={model.download_url}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full rounded-xl bg-gradient-to-br from-tg-button to-tg-button/80 py-3 text-center text-sm font-semibold text-tg-button-text shadow-lg shadow-tg-button/20 transition-all duration-200 active:scale-[0.98]"
        >
          {t('search.detail.downloadModel')}
        </a>
      )}
    </div>
  )
}
